import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import useToast from "../../hooks/useToast";
import useAuth from "../../hooks/useAuth";
import { meetingNoteService } from "../../api/meetingNoteService";
import logger from "../../utils/logger";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { FileText, Plus, Clock, User, Edit2, Trash2, Eye } from "lucide-react";

const MeetingNotesList = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { auth } = useAuth();
  const currentUserId = auth?.employee?._id;
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const fetchMeetingNotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await meetingNoteService.getMeetingNotes();
      setMeetingNotes(response.data?.data || response.data || []);
    } catch (error) {
      logger.error("Failed to fetch meeting notes:", error);
      showToast("Failed to load meeting notes", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchMeetingNotes();
  }, [fetchMeetingNotes]);

  useEffect(() => {
    const handler = () => fetchMeetingNotes();
    window.addEventListener("meeting-note-added", handler);
    return () => window.removeEventListener("meeting-note-added", handler);
  }, [fetchMeetingNotes]);

  const handleDeleteClick = (note, e) => {
    e.stopPropagation();
    setNoteToDelete(note);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;
    try {
      await meetingNoteService.deleteMeetingNote(noteToDelete._id);
      showToast("Meeting note deleted successfully", "success");
      setNoteToDelete(null);
      fetchMeetingNotes();
    } catch (error) {
      logger.error("Failed to delete meeting note:", error);
      showToast(
        error.response?.data?.message || "Failed to delete meeting note",
        "error",
      );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getEmployeeName = (employee) => {
    return employee?.fullNameEn || employee?.nickname || "Unknown";
  };

  const handleCreateNote = async () => {
    try {
      setCreating(true);
      const response = await meetingNoteService.createMeetingNote({
        title: "Untitled",
        viewers: [],
        editors: [],
      });
      const newId = response.data?.data?._id || response.data?._id;
      if (newId) {
        showToast("Note created", "success");
        navigate(`/communication/meeting-notes/${newId}`);
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to create note",
        "error",
      );
    } finally {
      setCreating(false);
    }
  };

  const DocumentCardSkeleton = () => (
    <div className="bg-white dark:bg-primary-card p-6 rounded-xl border border-gray-200 dark:border-dark-100 shadow-sm animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-3">
          <div className="h-6 bg-gray-200 dark:bg-dark-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-dark-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 dark:bg-dark-200 rounded w-1/3"></div>
        </div>
        <div className="w-10 h-10 bg-gray-200 dark:bg-dark-200 rounded-full"></div>
      </div>
    </div>
  );

  return (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-light-100 dark:text-light-100">
              Notes
            </h1>
            <p className="text-sm text-light-500 dark:text-light-500 mt-1">
              Create and manage your notes and documentation
            </p>
          </div>
          <Button
            onClick={handleCreateNote}
            icon={<Plus className="w-4 h-4" />}
            disabled={creating}
          >
            {creating ? "Creating..." : "New Note"}
          </Button>
        </div>

        {/* Meeting Notes List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <DocumentCardSkeleton key={i} />
            ))}
          </div>
        ) : meetingNotes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-primary-card p-12 rounded-xl border border-gray-200 dark:border-dark-100 shadow-sm text-center"
          >
            <FileText className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-light-100 dark:text-light-100 mb-2">
              No notes yet
            </h3>
            <p className="text-light-500 dark:text-light-500 mb-6">
              Create your first note to get started
            </p>
            <Button
              onClick={handleCreateNote}
              icon={<Plus className="w-4 h-4" />}
              disabled={creating}
            >
              {creating ? "Creating..." : "Create Note"}
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meetingNotes.map((note, index) => (
              <motion.div
                key={note._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() =>
                  navigate(`/communication/meeting-notes/${note._id}`)
                }
                className="bg-white dark:bg-primary-card p-6 rounded-xl border border-gray-200 dark:border-dark-100 shadow-sm cursor-pointer hover:shadow-glow hover:border-primary-accent/50 transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-light-100 dark:text-light-100 mb-2 line-clamp-2">
                      {note.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-light-500 dark:text-light-500">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{getEmployeeName(note.createdBy)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(note.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {note.viewers && note.viewers.length > 0 && (
                      <div
                        className="flex items-center gap-1 text-xs text-light-500 dark:text-light-500"
                        title={`${note.viewers.length} viewer${note.viewers.length !== 1 ? "s" : ""}`}
                      >
                        <Eye className="w-3 h-3" />
                        <span>{note.viewers.length}</span>
                      </div>
                    )}
                    {note.editors && note.editors.length > 0 && (
                      <div
                        className="flex items-center gap-1 text-xs text-light-500 dark:text-light-500"
                        title={`${note.editors.length} editor${note.editors.length !== 1 ? "s" : ""}`}
                      >
                        <Edit2 className="w-3 h-3" />
                        <span>{note.editors.length}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-dark-100">
                  <span className="text-xs text-light-500 dark:text-light-500">
                    {note.tabs?.length || 0} tab
                    {note.tabs?.length !== 1 ? "s" : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/communication/meeting-notes/${note._id}`);
                      }}
                      className="p-2 text-primary-accent hover:bg-primary-accent/10 dark:hover:bg-primary-accent/20 rounded-lg transition-colors"
                      title="Open"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    {currentUserId &&
                      note.createdBy &&
                      (typeof note.createdBy === "object"
                        ? note.createdBy._id?.toString() ===
                          currentUserId.toString()
                        : note.createdBy.toString() ===
                          currentUserId.toString()) && (
                        <button
                          onClick={(e) => handleDeleteClick(note, e)}
                          className="p-2 text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete note"
        message={
          noteToDelete
            ? `Are you sure you want to delete "${noteToDelete.title}"? This action cannot be undone.`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </PageWrapper>
  );
};

export default MeetingNotesList;
